/**
 * 숫자 입력 — 엑셀식 `=` 사칙연산 지원 (공용 컴포넌트).
 *
 *   - 입력 중에는 자유 텍스트(=18525000/1.1, =150*2, 1,000, -500 등)
 *   - Enter / 다른 칸 클릭(blur) 시 계산해서 숫자로 확정 (normalizeAmountInput)
 *   - decimals: 확정값 소수 자리수 (기본 0=원 단위). 단가처럼 DB 가 소수를
 *     저장하는 칸은 2 — 계산은 소수로 정확히, **표시는 항상 정수(반올림+콤마)**.
 *   - 포커스 시 실제 값(소수 포함) 그대로 노출 — 확인·수정 가능.
 *   - min 지정 시 그 미만 확정값은 min 으로 보정 (매입 수량·단가 음수 방지).
 *   - 식 오류(0 나누기·불완전한 식) 시 빨강 표시, 값 미확정.
 *
 * QuoteFormModal 의 로컬 구현(2026-06-02 #182)을 공용 추출 + decimals/min 옵션 (2026-06-10).
 */
import { useEffect, useState } from 'react';
import { normalizeAmountInput } from '../lib/evalAmount';

/** 천 단위 콤마 + 정수 반올림 표시 (포커스 아닐 때). */
function fmtIntComma(n: number): string {
  return Math.round(Number(n) || 0).toLocaleString('ko-KR');
}

export function CalcNumInput({
  value,
  onCommit,
  decimals = 0,
  min,
  className,
  ariaLabel,
}: {
  value: number;
  onCommit: (n: number) => void;
  /** 확정값 소수 자리수 (기본 0). 예: 단가 2 → =18525000/1.1 이 16840909.09 로 저장. */
  decimals?: number;
  /** 확정값 하한 (예: 0 — 음수 입력 방지). 미지정 시 음수 허용(견적 네고 등). */
  min?: number;
  className?: string;
  ariaLabel?: string;
}) {
  const [text, setText] = useState<string>(() => fmtIntComma(value));
  const [focused, setFocused] = useState(false);
  const [err, setErr] = useState(false);

  // 외부 값 변경(자동완성 단가 채움 등) → 포커스 아닐 때만 동기화
  useEffect(() => {
    if (!focused) setText(fmtIntComma(value));
  }, [value, focused]);

  function commit() {
    const normalized = normalizeAmountInput(text, decimals);
    if (normalized === null) {
      setErr(true);
      return;
    }
    setErr(false);
    let n = Number(normalized);
    if (min !== undefined && n < min) n = min;
    onCommit(n);
    setText(fmtIntComma(n));
  }

  return (
    <input
      type="text"
      aria-label={ariaLabel}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        if (err) setErr(false);
      }}
      onFocus={() => {
        setFocused(true);
        // 표시(정수 반올림)가 아니라 실제 값(소수 포함)을 편집 대상으로 노출
        setText(String(Number(value) || 0));
      }}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          commit();
        }
      }}
      title="=18525000/1.1 같은 수식 입력 가능 (Enter 로 계산)"
      className={`${className ?? ''} ${err ? 'border-rose-400 bg-rose-50' : ''}`}
    />
  );
}
